import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Check, Languages, Plus, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import {
  useCreateInvitation,
  useCreateOrganization,
  useCreateSite,
  useCreateTank,
} from "@/api/hooks";
import { qk } from "@/api/keys";
import { ApiError } from "@/api/problem";
import type { CurrentUser, OrganizationRole, Site } from "@/api/types";
import { useSession } from "@/auth/session";
import { Logo } from "@/components/logo";
import { SightGlass } from "@/components/ui/sight-glass";
import { Button, Field, Input, MicroLabel, NumberInput } from "@/components/ui/primitives";
import { Select } from "@/components/ui/select";
import { FormError, LoadingBlock } from "@/components/ui/states";
import type { TKey } from "@/i18n/dict";
import { useI18n } from "@/i18n/provider";

/**
 * First run.
 *
 * A brand-new customer has an identity and nothing else. Three steps, and each one leaves
 * something real behind: a workspace, a team, and a site with a tank that has fuel in it.
 *
 * The last step matters most. An onboarding that ends on an empty dashboard has failed —
 * the operator arrives at the screen this product exists for and it has nothing to say to
 * them. So step 3 does not finish until there is a TANK, because a tank is the only thing
 * the dashboard can actually read.
 */

const STEPS: ReadonlyArray<{ n: number; label: TKey }> = [
  { n: 1, label: "onboarding.rail.org" },
  { n: 2, label: "onboarding.rail.team" },
  { n: 3, label: "onboarding.rail.site" },
];

function OnboardingLayout({
  step,
  title,
  subtitle,
  children,
}: {
  step: number;
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  const { t, lang, setLang } = useI18n();

  return (
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="flex items-center justify-between px-6 py-5">
        <Logo className="h-6 text-cream" />

        {/* The same switch as the sign-in screen, in the same place. Half this product's
            users reach for it before they reach for anything else, and a person who has
            just signed up has not yet been given a settings screen to hide it in. */}
        <button
          type="button"
          onClick={() => setLang(lang === "ar" ? "en" : "ar")}
          aria-label={t("lang.switchTo")}
          className="inline-flex items-center gap-1.5 rounded-[8px] border border-rule px-2.5 py-1 font-mono text-[11px] text-dim transition-colors hover:border-mauve hover:text-text"
        >
          <Languages className="size-3.5" aria-hidden />
          {t("lang.toggleShort")}
        </button>
      </header>

      <div className="flex flex-1 items-start justify-center px-4 pb-12">
        <div className="w-full max-w-lg">
          {/* The rail is the sight glass laid on its side: it fills as they go, in the
              colour of fuel. The step is also NAMED — a bare "2 of 3" tells you how much
              is left but not what it is, and this is the one place in the product where
              numbered steps are honest, so they may as well be legible. */}
          <div className="mb-6">
            <div className="flex gap-1.5" aria-hidden>
              {STEPS.map((s) => (
                <span
                  key={s.n}
                  className={
                    "h-1 flex-1 rounded-full transition-colors duration-500 " +
                    (s.n <= step ? "bg-cream" : "bg-rule")
                  }
                />
              ))}
            </div>

            <div className="mt-2.5 flex items-center justify-between">
              <MicroLabel>{t(STEPS[step - 1]!.label)}</MicroLabel>
              <MicroLabel>
                <span className="num">{t("onboarding.step", { n: step, total: STEPS.length })}</span>
              </MicroLabel>
            </div>
          </div>

          <div className="rounded-[10px] border border-rule bg-paper p-6">
            <h1 className="text-xl leading-tight font-semibold text-text weight-display">
              {title}
            </h1>
            <p className="mt-1.5 text-[15px] leading-relaxed text-muted">{subtitle}</p>
            <div className="mt-7">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Steps 2 and 3 are tenant-scoped: their hooks call `useActiveOrgId()`, which throws when
 * there is no organization. That is the correct behaviour — it is a guard, not a bug — but
 * it means a person who deep-links to `/onboarding/team` before creating an organization
 * would hit a thrown error instead of a screen. Send them to step 1, where they belong.
 *
 * The check must happen in a PARENT of the component that calls those hooks: a hook cannot
 * be skipped, so the tenant-scoped screen must not be rendered at all.
 */
function RequireOrganization({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoading, me, activeOrgId: sessionOrgId } = useSession();

  // Read the org from the query CACHE, not only the session snapshot.
  //
  // The snapshot is a React value from the provider's last render; the cache can be a tick
  // ahead of it. Creating an organization does exactly that — it switches the session and
  // refetches /v1/me, then navigates HERE in the same tick, before the provider above has
  // re-rendered. Reading only the snapshot, this guard saw no org and bounced the customer
  // straight back to the create-organization form they had just completed — for an ARABIC
  // org especially, which is how the "adding an organization doesn't work" report started.
  // getQueryData is the same cache entry, one tick fresher.
  const cachedOrgId = queryClient.getQueryData<CurrentUser | null>(qk.me())
    ?.active_organization?.id;
  const activeOrgId = sessionOrgId ?? cachedOrgId;

  // `me` must be RESOLVED, not merely "not loading", before we act on its absence.
  const known = !isLoading && me !== undefined && me !== null;

  useEffect(() => {
    if (known && !activeOrgId) void navigate({ to: "/onboarding/organization" });
  }, [known, activeOrgId, navigate]);

  if (!activeOrgId) return <LoadingBlock />;
  return <>{children}</>;
}

/* ------------------------------------------------------- 1. create org */

/** The slug rule, straight from the spec's `CreateOrganizationRequest.slug` pattern. */


export function CreateOrganizationPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const createOrg = useCreateOrganization();

  const [name, setName] = useState("");

  // NO SLUG FIELD. You type your organization's NAME — in any language — and that is all.
  //
  // The slug (the workspace address) is the server's job. It derives a pretty one from a
  // Latin name, and mints a guaranteed-unique fallback for a name that doesn't transliterate
  // — which is EVERY Arabic name, i.e. the product's primary audience. Asking the customer
  // to hand-type a Latin URL-slug before they can create a workspace was a wall: an Arabic
  // name auto-derived to nothing, the field could not be satisfied, and the Create button
  // never enabled. The address can be renamed later in settings for anyone who wants a
  // prettier one. A working org today beats a beautiful URL never.
  const fieldErrors = createOrg.error instanceof ApiError ? createOrg.error.fieldErrors : {};

  return (
    <OnboardingLayout
      step={1}
      title={t("onboarding.createOrg.title")}
      subtitle={t("onboarding.createOrg.subtitle")}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          createOrg.mutate(
            { name: name.trim() },
            { onSuccess: () => void navigate({ to: "/onboarding/team" }) },
          );
        }}
        className="flex flex-col gap-5"
        noValidate
      >
        <FormError
          error={
            createOrg.error instanceof ApiError && createOrg.error.isFormLevel
              ? createOrg.error
              : null
          }
        />

        <Field
          label={t("onboarding.createOrg.name")}
          error={fieldErrors["name"]}
          hint={t("onboarding.createOrg.nameHint")}
          required
        >
          {(props) => (
            <Input
              {...props}
              required
              autoFocus
              placeholder={t("onboarding.createOrg.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Button
          type="submit"
          disabled={createOrg.isPending || !name.trim()}
          className="w-full"
        >
          {createOrg.isPending ? t("common.saving") : t("onboarding.createOrg.submit")}
        </Button>
      </form>
    </OnboardingLayout>
  );
}

/* ---------------------------------------------------------- 2. invite team */

const ROLE_OPTIONS: ReadonlyArray<{ value: OrganizationRole; label: TKey; hint: TKey }> = [
  { value: "admin", label: "role.admin", hint: "role.admin.hint" },
  { value: "operator", label: "role.operator", hint: "role.operator.hint" },
  { value: "viewer", label: "role.viewer", hint: "role.viewer.hint" },
];

interface InviteDraft {
  email: string;
  role: OrganizationRole;
}

export function InviteTeamPage() {
  return (
    <RequireOrganization>
      <InviteTeamForm />
    </RequireOrganization>
  );
}

function InviteTeamForm() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const invite = useCreateInvitation();

  const [drafts, setDrafts] = useState<InviteDraft[]>([{ email: "", role: "operator" }]);
  const [sentCount, setSentCount] = useState(0);

  const options = ROLE_OPTIONS.map((r) => ({
    value: r.value,
    label: t(r.label),
    hint: t(r.hint),
  }));

  const update = (index: number, patch: Partial<InviteDraft>) => {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));
  };

  const filled = drafts.filter((d) => d.email.trim() !== "");

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // They are alone in a new product. Nobody should have to invent a colleague to get
    // past this screen — an empty form here means "next", not "error".
    if (filled.length === 0) {
      void navigate({ to: "/onboarding/site" });
      return;
    }

    // Sequential, not parallel: each invitation is its own row and its own email, and if
    // the third fails the first two were genuinely sent. Reporting "all or nothing" would
    // be a lie about what the server did.
    let sent = 0;
    for (const draft of filled) {
      try {
        await invite.mutateAsync({ email: draft.email.trim(), role: draft.role });
        sent++;
      } catch {
        // Surfaced below. Keep going: one bad address must not block the rest.
      }
    }
    setSentCount(sent);
    if (sent === filled.length) void navigate({ to: "/onboarding/site" });
  };

  return (
    <OnboardingLayout
      step={2}
      title={t("onboarding.invite.title")}
      subtitle={t("onboarding.invite.subtitle")}
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormError error={invite.error} />

        {sentCount > 0 ? (
          <p role="status" className="flex items-center gap-1.5 text-sm text-ok">
            <Check className="size-4" aria-hidden />
            <span className="num">{t("onboarding.invite.sent", { n: sentCount })}</span>
          </p>
        ) : null}

        <div className="flex flex-col gap-3">
          {drafts.map((draft, index) => (
            // On a phone an email field squeezed next to a role select is too narrow to
            // read what you typed. Below `sm` they stack; the email gets the whole width.
            <div key={index} className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-end">
              <Field label={t("settings.invite.email")} className="flex-1">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    dir="ltr"
                    placeholder="omar@your-org.com"
                    value={draft.email}
                    onChange={(e) => update(index, { email: e.target.value })}
                  />
                )}
              </Field>

              <Field label={t("settings.invite.role")} className="w-full sm:w-40">
                {(props) => (
                  // `Select` is h-10 and `Input` is h-11, so side by side the two labels sit
                  // on different baselines and the row looks broken. Matched here at the
                  // call site; the primitives themselves are not mine to change.
                  <Select
                    {...props}
                    className="h-11"
                    value={draft.role}
                    onValueChange={(value) => update(index, { role: value as OrganizationRole })}
                    options={options}
                  />
                )}
              </Field>

              {drafts.length > 1 ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="self-end sm:mb-0.5"
                  aria-label={t("common.delete")}
                  onClick={() => setDrafts((prev) => prev.filter((_, i) => i !== index))}
                >
                  <X className="size-4" aria-hidden />
                </Button>
              ) : null}
            </div>
          ))}
        </div>

        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          onClick={() => setDrafts((prev) => [...prev, { email: "", role: "operator" }])}
        >
          <Plus className="size-4" aria-hidden />
          {t("onboarding.invite.add")}
        </Button>

        <div className="mt-2 flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => void navigate({ to: "/onboarding/site" })}>
            {t("onboarding.invite.later")}
          </Button>

          <Button type="submit" disabled={invite.isPending}>
            {invite.isPending
              ? t("common.saving")
              : filled.length > 0
                ? t("onboarding.invite.send")
                : t("common.next")}
          </Button>
        </div>
      </form>
    </OnboardingLayout>
  );
}

/* -------------------------------------------------- 3. first site + tank */

export function FirstSitePage() {
  return (
    <RequireOrganization>
      <FirstSiteFlow />
    </RequireOrganization>
  );
}

/**
 * Step 3 is two halves, and the seam between them is real, not cosmetic: the site is
 * genuinely created on the server before the tank form appears, because a tank cannot be
 * posted without a `siteId` to hang it on.
 *
 * So the site half collapses into a completed line — it IS done — and the tank half opens
 * beneath it. If the tank then fails, the site is still there and only the tank is retried.
 * Rolling the site back to keep the form tidy would delete something the customer made.
 */
function FirstSiteFlow() {
  const [site, setSite] = useState<Site | null>(null);

  return site ? <FirstTankForm site={site} /> : <FirstSiteForm onCreated={setSite} />;
}

function FirstSiteForm({ onCreated }: { onCreated: (site: Site) => void }) {
  const { t } = useI18n();
  const createSite = useCreateSite();

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [address, setAddress] = useState("");

  const fieldErrors = createSite.error instanceof ApiError ? createSite.error.fieldErrors : {};

  return (
    <OnboardingLayout
      step={3}
      title={t("onboarding.site.title")}
      subtitle={t("onboarding.site.subtitle")}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          createSite.mutate(
            {
              name: name.trim(),
              ...(code.trim() ? { code: code.trim() } : {}),
              ...(address.trim() ? { address: address.trim() } : {}),
            },
            { onSuccess: onCreated },
          );
        }}
        className="flex flex-col gap-5"
        noValidate
      >
        <FormError
          error={
            createSite.error instanceof ApiError && createSite.error.isFormLevel
              ? createSite.error
              : null
          }
        />

        <Field label={t("sites.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input
              {...props}
              required
              autoFocus
              placeholder="فرع الكرادة"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <Field label={t("sites.code")} error={fieldErrors["code"]} hint={t("common.optional")}>
          {(props) => (
            <Input
              {...props}
              dir="ltr"
              placeholder="KRD-01"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          )}
        </Field>

        <Field
          label={t("sites.address")}
          error={fieldErrors["address"]}
          hint={t("common.optional")}
        >
          {(props) => (
            <Input {...props} value={address} onChange={(e) => setAddress(e.target.value)} />
          )}
        </Field>

        <Button type="submit" disabled={createSite.isPending || !name.trim()} className="w-full">
          {createSite.isPending ? t("common.saving") : t("onboarding.site.submit")}
        </Button>
      </form>
    </OnboardingLayout>
  );
}

/**
 * The tank, and the end of the road.
 *
 * There is a live sight glass beside the numbers. It is not decoration: it is the first
 * time this person sees the instrument their whole account is built around, and it moves
 * as they type. By the time they land on the dashboard they already know how to read it.
 */
function FirstTankForm({ site }: { site: Site }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const createTank = useCreateTank(site.id);

  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  const [threshold, setThreshold] = useState("20");
  const [initial, setInitial] = useState("");

  const fieldErrors = createTank.error instanceof ApiError ? createTank.error.fieldErrors : {};

  const capacityNum = Number(capacity);
  const initialNum = Number(initial);
  const thresholdNum = Number(threshold);

  const hasCapacity = capacity !== "" && capacityNum > 0;
  const percent =
    hasCapacity && initial !== "" && initialNum >= 0
      ? Math.min((initialNum / capacityNum) * 100, 100)
      : null;

  const overCapacity = hasCapacity && initial !== "" && initialNum > capacityNum;

  return (
    <OnboardingLayout
      step={3}
      title={t("onboarding.tank.title")}
      subtitle={t("onboarding.tank.subtitle")}
    >
      {/* The site is made. Saying so — by name — is the difference between a three-part
          form and three steps that each landed. */}
      <p className="mb-6 flex items-center gap-2 rounded-[8px] border border-rule-strong bg-paper-sunk px-3 py-2.5 text-sm text-text">
        <Check className="size-4 shrink-0 text-ok" aria-hidden />
        {t("onboarding.site.created", { name: site.name })}
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          createTank.mutate(
            {
              name: name.trim(),
              capacity_liters: capacityNum,
              ...(threshold !== "" ? { low_level_threshold_percent: thresholdNum } : {}),
              ...(initial !== "" ? { initial_level_liters: initialNum } : {}),
            },
            {
              // Onto the dashboard — which now has a site, a tank and a level on it. That
              // is the whole point of making them do this before they get there.
              onSuccess: () => void navigate({ to: "/" }),
            },
          );
        }}
        className="flex flex-col gap-5"
        noValidate
      >
        <FormError
          error={
            createTank.error instanceof ApiError && createTank.error.isFormLevel
              ? createTank.error
              : null
          }
        />

        <Field label={t("tank.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input
              {...props}
              required
              autoFocus
              placeholder="الخزّان الرئيسي"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>

        <div className="flex items-start gap-5">
          <div className="flex flex-1 flex-col gap-5">
            <Field
              label={`${t("tank.capacity")} (${t("common.liters")})`}
              error={fieldErrors["capacity_liters"]}
              hint={t("onboarding.tank.capacityHint")}
              required
            >
              {(props) => (
                <NumberInput
                  {...props}
                  required
                  min={1}
                  placeholder="10000"
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              )}
            </Field>

            <Field
              label={`${t("tank.threshold")} (%)`}
              error={fieldErrors["low_level_threshold_percent"]}
              hint={t("tank.thresholdHint")}
            >
              {(props) => (
                <NumberInput
                  {...props}
                  min={1}
                  max={90}
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                />
              )}
            </Field>

            <Field
              label={`${t("tank.initialLevel")} (${t("common.liters")})`}
              error={
                overCapacity
                  ? t("refills.checkOverCapacity", { capacity: capacityNum })
                  : fieldErrors["initial_level_liters"]
              }
              hint={t("onboarding.tank.initialHint")}
            >
              {(props) => (
                <NumberInput
                  {...props}
                  min={0}
                  placeholder="0"
                  value={initial}
                  onChange={(e) => setInitial(e.target.value)}
                />
              )}
            </Field>
          </div>

          {/* The instrument, live. Hidden from assistive tech only because everything it
              says is already said by the fields it is reading. */}
          <div className="hidden flex-col items-center gap-2 pt-7 sm:flex">
            <SightGlass
              percent={percent}
              level={percent !== null && percent <= thresholdNum ? "low" : "ok"}
              className="h-40 w-9"
              label={t("tank.level")}
            />
            <span className="num text-xs text-dim">
              {percent === null ? "—" : `${Math.round(percent)}%`}
            </span>
          </div>
        </div>

        <Button
          type="submit"
          disabled={createTank.isPending || !name.trim() || !hasCapacity || overCapacity}
          className="w-full"
        >
          {createTank.isPending ? t("onboarding.tank.finishing") : t("onboarding.tank.submit")}
        </Button>
      </form>
    </OnboardingLayout>
  );
}

/**
 * The gate. A signed-in user with no organization has nothing to look at and nothing to
 * do, so they go straight to step 1 — the first-run branch of the whole product.
 */
export function useOnboardingRedirect() {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, activeOrgId } = useSession();

  useEffect(() => {
    if (!isLoading && isAuthenticated && !activeOrgId) {
      void navigate({ to: "/onboarding/organization" });
    }
  }, [isLoading, isAuthenticated, activeOrgId, navigate]);
}
